import { useAuth, useClerk, useUser } from "@clerk/tanstack-react-start";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import {
  User,
  Search,
  ChevronDown,
  Sparkles,
  Settings,
  LogOut,
  LogIn,
  LayoutDashboard,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from "react";

import { useBodyScrollLock } from "~/hooks/use-body-scroll-lock";
import { useHideOnScroll } from "~/hooks/use-hide-on-scroll";
import ImageLogoHorizontalUrl from "~/img/logo/dcm-horizontal-1.png";
import { LogoIcon } from "~/img/logo/icon";
import { signOut } from "~/lib/clerk";
import { cn } from "~/lib/cn";
import {
  trackNavClick,
  type NavClickLocation,
} from "~/lib/monitoring/track-event";
import {
  navConfig,
  type NavItemConfig,
  type NavGroup,
  type NavLink,
} from "~/lib/nav-config";
import { coachStatusQueryOptions } from "~/lib/queries/coachStatusQuery";

import { Button, ButtonLink, ButtonRouter } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./navigation-menu";
import { Skeleton } from "./skeleton";
import { SummitTopBanner } from "./summit-top-banner";

type DashboardRoute = "/dashboard" | "/my-coaching";

const accountLinkClassName =
  "flex items-center rounded-xl px-3 py-3 text-sm font-medium text-gray-900 transition-colors active:bg-brand-50";

const navLinkKey = (link: NavLink): string =>
  link.params ? `${link.to}:${Object.values(link.params).join(",")}` : link.to;

/**
 * Reports the rendered href rather than the route template, so `/page/$name`
 * links reach analytics as the page the visitor actually opened.
 */
const navClickHandler =
  (location: NavClickLocation, label: string, onNavigate?: () => void) =>
  (event: MouseEvent<HTMLAnchorElement>): void => {
    trackNavClick({
      location,
      label,
      destination: event.currentTarget.getAttribute("href") ?? "",
    });
    onNavigate?.();
  };

// Single link item inside a mega menu panel
const MegaLink = ({ link }: { link: NavLink }) => {
  const Icon = link.icon;
  return (
    <NavigationMenuLink asChild>
      <Link
        to={link.to}
        params={link.params}
        onClick={navClickHandler("header", link.label)}
        className="group/link flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-accent"
      >
        {Icon && (
          <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600 transition-colors group-hover/link:bg-brand-100">
            <Icon className="size-4" />
          </div>
        )}
        <div className="min-w-0 space-y-1">
          <div className="truncate text-sm leading-snug font-medium">
            {link.label}
          </div>
          {link.description && (
            <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
              {link.description}
            </p>
          )}
        </div>
      </Link>
    </NavigationMenuLink>
  );
};

// Column group inside a mega menu panel
const MegaGroup = ({ group }: { group: NavGroup }) => (
  <div className="min-w-0">
    <h4 className="mb-2 truncate px-3 text-xs font-medium tracking-wider text-muted-foreground uppercase">
      {group.title}
    </h4>
    <ul className="space-y-0.5">
      {group.links.map((link) => (
        <li key={navLinkKey(link)}>
          <MegaLink link={link} />
        </li>
      ))}
    </ul>
  </div>
);

// Desktop nav item (renders either a plain link or a mega menu trigger)
const DesktopNavItem = ({ item }: { item: NavItemConfig }) => {
  if (item.type === "link") {
    return (
      <NavigationMenuItem>
        <NavigationMenuLink asChild>
          <Link
            to={item.to}
            params={item.params}
            onClick={navClickHandler("header", item.label)}
            className="inline-flex h-9 items-center justify-center rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            {item.label}
          </Link>
        </NavigationMenuLink>
      </NavigationMenuItem>
    );
  }

  const { featured } = item;
  const colCount = item.groups.length + (featured ? 1 : 0);

  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger className="whitespace-nowrap">
        {item.label}
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <div
          className={cn(
            "grid gap-6 p-6",
            colCount <= 2 && "grid-cols-2",
            colCount === 3 && "grid-cols-3",
            colCount >= 4 && "grid-cols-4",
          )}
        >
          {featured && (
            <Link
              to={featured.to}
              params={featured.params}
              onClick={navClickHandler("header", featured.title)}
              className="group/featured row-span-3 flex flex-col justify-between rounded-lg bg-gradient-to-b from-brand-50 to-brand-100/50 p-5 no-underline transition-colors hover:from-brand-100 hover:to-brand-200/50"
            >
              <div>
                <Sparkles className="mb-3 size-5 text-brand-600" />
                <div className="mb-2 text-base leading-tight font-medium">
                  {featured.title}
                </div>
                <p className="text-sm leading-snug text-muted-foreground">
                  {featured.description}
                </p>
              </div>
              <span className="mt-4 inline-flex items-center text-sm font-medium text-brand-600 transition-colors group-hover/featured:text-brand-700">
                {featured.ctaLabel}
                <span className="ml-1 transition-transform group-hover/featured:translate-x-0.5">
                  &rarr;
                </span>
              </span>
            </Link>
          )}
          {item.groups.map((group) => (
            <MegaGroup key={group.title} group={group} />
          ))}
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};

// Mobile link with icon
const MobileLink = ({
  link,
  onNavigate,
}: {
  link: NavLink;
  onNavigate: () => void;
}) => {
  const Icon = link.icon;
  return (
    <Link
      to={link.to}
      params={link.params}
      onClick={navClickHandler("mobile_menu", link.label, onNavigate)}
      className="flex items-center gap-3 rounded-xl px-3 py-3 transition-colors active:bg-brand-50"
    >
      {Icon && (
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Icon className="size-4" />
        </div>
      )}
      <div className="min-w-0">
        <div className="text-sm leading-snug font-medium text-gray-900">
          {link.label}
        </div>
        {link.description && (
          <p className="truncate text-xs leading-snug text-muted-foreground">
            {link.description}
          </p>
        )}
      </div>
    </Link>
  );
};

// Mobile collapsible nav section
const MobileNavItem = ({
  item,
  onNavigate,
}: {
  item: NavItemConfig;
  onNavigate: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (item.type === "link") {
    return (
      <Link
        to={item.to}
        params={item.params}
        onClick={navClickHandler("mobile_menu", item.label, onNavigate)}
        className="flex items-center rounded-xl px-3 py-3 text-sm font-medium text-gray-900 transition-colors active:bg-brand-50"
      >
        {item.label}
      </Link>
    );
  }

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between rounded-xl px-3 py-3 text-sm font-medium text-gray-900 transition-colors active:bg-brand-50"
      >
        <span>{item.label}</span>
        <ChevronDown
          className={cn(
            "size-4 text-muted-foreground transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <div className="space-y-4 pt-1 pb-2">
            {item.featured && (
              <Link
                to={item.featured.to}
                params={item.featured.params}
                onClick={navClickHandler(
                  "mobile_menu",
                  item.featured.title,
                  onNavigate,
                )}
                className="mx-1 flex items-center gap-3 rounded-xl bg-gradient-to-r from-brand-50 to-brand-100/50 p-4 transition-colors active:from-brand-100 active:to-brand-200/50"
              >
                <Sparkles className="size-5 shrink-0 text-brand-600" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {item.featured.title}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {item.featured.ctaLabel} &rarr;
                  </p>
                </div>
              </Link>
            )}
            {item.groups.map((group) => (
              <div key={group.title}>
                <h4 className="mb-1 px-3 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                  {group.title}
                </h4>
                {group.links.map((link) => (
                  <MobileLink
                    key={navLinkKey(link)}
                    link={link}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// User dropdown component
const UserDropdown = ({
  dashboardTo,
  onSignOut,
}: {
  dashboardTo: DashboardRoute | null;
  onSignOut: () => void;
}) => {
  const { user } = useUser();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex h-9 w-9 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-brand-100 text-brand-500 ring-2 ring-transparent transition hover:ring-brand-200 active:ring-brand-300">
        {user?.imageUrl ? (
          <img
            src={user.imageUrl}
            alt={user.fullName ?? "User avatar"}
            className="h-full w-full object-cover"
          />
        ) : (
          <User className="size-4" />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex items-center gap-3 p-3 font-normal">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-100 text-brand-500">
            {user?.imageUrl ? (
              <img
                src={user.imageUrl}
                alt={user.fullName ?? "User avatar"}
                className="h-full w-full object-cover"
              />
            ) : (
              <User className="size-5" />
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">
              {user?.fullName ?? "User"}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {user?.primaryEmailAddress?.emailAddress}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {dashboardTo && (
          <DropdownMenuItem asChild>
            <Link to={dashboardTo}>
              <LayoutDashboard className="mr-2 size-4" />
              My Dashboard
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <Link to="/settings">
            <Settings className="mr-2 size-4" />
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onSignOut} className="text-muted-foreground">
          <LogOut className="mr-2 size-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

function DefaultHeader() {
  const { isSignedIn } = useAuth();
  const { user, isLoaded: isUserLoaded } = useUser();
  const clerk = useClerk();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // On mobile the bar is sticky and retracts as you scroll down; pinned open
  // while the menu is up so the close affordance can't scroll away.
  const { hidden, scrolled } = useHideOnScroll({ pinned: isMenuOpen });
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const { data: coachStatus, isLoading: isCoachQueryLoading } = useQuery(
    coachStatusQueryOptions(user?.id),
  );
  const isCoachStatusLoading = !isUserLoaded || isCoachQueryLoading;
  // Null until coach status resolves: a guess would send coaches to the member
  // dashboard. Anyone with a coach record — pending, approved, or revoked —
  // lands on the coach home, which routes to Welcome, the coach dashboard, or
  // the revoked card.
  const dashboardTo: DashboardRoute | null = isCoachStatusLoading
    ? null
    : coachStatus?.isCoach
      ? "/my-coaching"
      : "/dashboard";

  useBodyScrollLock(isMenuOpen);

  // The mobile sheet drops below the bar, whose height varies with the summit
  // banner — measure it instead of hard-coding an offset.
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) =>
      setBarHeight(entry.contentRect.height),
    );
    observer.observe(el);
    setBarHeight(el.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, []);

  // Publish how much vertical space the bar is currently occupying so nested
  // sticky sub-navs (CIH tab bars, article filters) can dock beneath it and
  // ride along as it retracts. Below `md` only — see `--dcm-sticky-offset`.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--dcm-header-h", `${hidden ? 0 : barHeight}px`);
    return () => {
      root.style.removeProperty("--dcm-header-h");
    };
  }, [hidden, barHeight]);

  // Navigating within the SPA keeps this component mounted, so the sheet has to
  // be dismissed explicitly.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMenuOpen]);

  const handleSignOut = async () => {
    try {
      if (!clerk) {
        return;
      }
      await signOut();
      await clerk.signOut();
    } catch {
      // Ignore sign out errors - user will be redirected anyway
    }
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full md:relative",
        hidden && "pointer-events-none md:pointer-events-auto",
      )}
    >
      {/*
          Everything that retracts lives in this wrapper — the mobile sheet is a
          sibling, because a translated ancestor would become the containing
          block for its `position: fixed` overlay. It also outranks the sheet
          (z-110 vs z-100) so the close button stays reachable above the
          backdrop.
        */}
      <div
        ref={barRef}
        className={cn(
          "relative z-110 transition-transform duration-300 ease-out motion-reduce:transition-none md:translate-y-0 md:transition-none",
          hidden ? "-translate-y-full" : "translate-y-0",
          scrolled &&
            "border-b border-gray-200/70 bg-white/90 backdrop-blur md:border-0 md:bg-transparent md:backdrop-blur-none",
        )}
      >
        <SummitTopBanner />
        <div className="relative z-50 mx-auto w-full max-w-7xl px-4 py-3 xl:px-0">
          {/* Desktop: NavigationMenu root wraps full header row so viewport spans full width */}
          <NavigationMenu className="hidden w-full max-w-none items-center gap-4 md:flex">
            {/* Logo — outside the List, inside the Root */}
            <div className="shrink-0">
              <a href="/" className="flex items-center">
                <img
                  src={ImageLogoHorizontalUrl}
                  className="h-[36px] w-auto"
                  alt="DreamCoachMatch"
                  decoding="sync"
                  loading="eager"
                />
              </a>
            </div>

            {/* Nav triggers */}
            <NavigationMenuList className="gap-1">
              {navConfig.map((item) => (
                <DesktopNavItem key={item.label} item={item} />
              ))}
            </NavigationMenuList>

            {/* Right-side actions */}
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {isSignedIn ? (
                <>
                  {dashboardTo ? (
                    <ButtonRouter
                      to={dashboardTo}
                      size="sm"
                      variant="primary"
                      className="gap-2"
                    >
                      <LayoutDashboard className="size-4" />
                      My Dashboard
                    </ButtonRouter>
                  ) : (
                    <Skeleton className="h-9 w-36 rounded-lg" />
                  )}
                  <UserDropdown
                    dashboardTo={dashboardTo}
                    onSignOut={handleSignOut}
                  />
                </>
              ) : (
                <>
                  <ButtonRouter
                    to="/login"
                    size="sm"
                    variant="default"
                    className="gap-2"
                  >
                    {/* Below lg the nav row is full, so the icons and the long label give way. */}
                    <LogIn className="hidden size-4 lg:block" />
                    Log in
                  </ButtonRouter>
                  <ButtonLink
                    href="/discovery"
                    size="sm"
                    variant="primary"
                    className="gap-2"
                  >
                    <Search className="hidden size-4 lg:block" />
                    <span className="lg:hidden">Get matched</span>
                    <span className="hidden lg:inline">
                      Find my dream coach
                    </span>
                  </ButtonLink>
                </>
              )}
            </div>
          </NavigationMenu>

          {/* Mobile header row */}
          <div className="flex items-center gap-2 md:hidden">
            <div className="shrink-0">
              <a
                href="/"
                className="flex items-center"
                aria-label="DreamCoachMatch home"
              >
                <LogoIcon className="h-10 w-10" aria-hidden="true" />
              </a>
            </div>

            {/* The bar follows you down the page, so the primary CTA rides along */}
            {!isSignedIn && (
              <ButtonLink
                href="/discovery"
                size="sm"
                variant="primary"
                className="ml-auto gap-2 px-3 text-xs"
              >
                <Search className="size-4" />
                Find a coach
              </ButtonLink>
            )}

            <Button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={cn(!isSignedIn ? "-mr-2" : "ml-auto")}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
              variant="ghost"
              size="icon-nav"
            >
              <div className="flex h-4 w-6 flex-col items-center justify-between">
                <span
                  className={cn(
                    "h-0.5 w-full rounded-full bg-brand-800 transition-transform",
                    isMenuOpen && "translate-y-[7px] rotate-45",
                  )}
                />
                <span
                  className={cn(
                    "h-0.5 w-full rounded-full bg-brand-800 transition-opacity",
                    isMenuOpen && "opacity-0",
                  )}
                />
                <span
                  className={cn(
                    "h-0.5 w-full rounded-full bg-brand-800 transition-transform",
                    isMenuOpen && "-translate-y-[7px] -rotate-45",
                  )}
                />
              </div>
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div
        className={cn(
          "fixed inset-0 z-100 transition duration-200 md:hidden",
          isMenuOpen
            ? "pointer-events-auto bg-gray-900/60 backdrop-blur-sm"
            : "backdrop-blur-0 pointer-events-none bg-transparent",
        )}
        onClick={closeMenu}
      >
        <div
          id="mobile-navigation"
          className={cn(
            "pointer-events-auto flex max-h-[85dvh] flex-col overflow-hidden rounded-b-2xl bg-white shadow-2xl transition duration-300 motion-reduce:transition-none",
            isMenuOpen
              ? "translate-y-0 opacity-100"
              : "-translate-y-full opacity-0",
          )}
          style={{ paddingTop: barHeight }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Scroll area */}
          <div className="flex-1 space-y-1 overflow-y-auto overscroll-contain px-4 pt-2 pb-4">
            {navConfig.map((item) => (
              <MobileNavItem
                key={item.label}
                item={item}
                onNavigate={closeMenu}
              />
            ))}

            {/* User section */}
            {isSignedIn && (
              <>
                <div className="mx-3 my-2 h-px bg-gray-100" />
                <h4 className="px-3 pt-1 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                  My Account
                </h4>
                {dashboardTo && (
                  <Link
                    to={dashboardTo}
                    onClick={closeMenu}
                    className={accountLinkClassName}
                  >
                    My Dashboard
                  </Link>
                )}
                <Link
                  to="/settings"
                  onClick={closeMenu}
                  className={accountLinkClassName}
                >
                  Settings
                </Link>
                <button
                  onClick={handleSignOut}
                  className="flex w-full items-center rounded-xl px-3 py-3 text-left text-sm font-medium text-muted-foreground transition-colors active:bg-red-50 active:text-red-600"
                >
                  Log out
                </button>
              </>
            )}
          </div>

          {/* Sticky CTA footer */}
          {!isSignedIn && (
            <div className="flex gap-2 border-t border-gray-100 bg-gray-50/80 px-4 pt-4 pb-safe-4">
              <ButtonRouter
                to="/login"
                size="default"
                variant="default"
                className="gap-2"
              >
                <LogIn className="size-4" />
                Log in
              </ButtonRouter>
              <ButtonLink
                href="/discovery"
                size="default"
                variant="primary"
                className="flex-1 gap-2"
              >
                <Search className="size-4" />
                Find my dream coach
              </ButtonLink>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function DiagnosticHeader() {
  return (
    <header className="w-full border-b border-gray-200/80 bg-white">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 xl:px-0">
        <a
          href="/"
          className="flex items-center"
          aria-label="DreamCoachMatch home"
        >
          <img
            src={ImageLogoHorizontalUrl}
            className="h-9 w-auto"
            alt="DreamCoachMatch"
            decoding="sync"
            loading="eager"
          />
        </a>
        <ButtonRouter to="/login" size="sm" variant="default">
          <LogIn className="size-4" />
          Log in
        </ButtonRouter>
      </div>
    </header>
  );
}

/** Site header with navigation; variant picks the default or the diagnostic layout. */
export const Header = ({
  variant = "default",
}: {
  variant?: "default" | "diagnostic";
}) => (variant === "diagnostic" ? <DiagnosticHeader /> : <DefaultHeader />);
